import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/transfert/chauffeur-transfert.png";
import { cityPriceFaq } from "./cityPricing";

const cogolin: ChauffeurPage = {
  slug: "cogolin",
  title: "Chauffeur privé à Cogolin – VTC porte du golfe",
  subtitle:
    "Chauffeur privé et VTC à Cogolin : transfert aéroport de Nice, gare de Saint-Raphaël, Marines de Cogolin, mise à disposition et liaison Saint-Tropez.",
  keywords: [
    "chauffeur privé cogolin",
    "vtc cogolin",
    "chauffeur cogolin",
    "transfert cogolin aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Cogolin",
    subtitle:
      "La porte du golfe de Saint-Tropez et ses Marines : transfert aéroport de Nice, mise à disposition et longue distance, 7j/7.",
    keywords: [
      "VTC Cogolin",
      "chauffeur Marines de Cogolin",
      "transfert aéroport Nice",
      "chauffeur golfe de Saint-Tropez",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Cogolin (golfe de Saint-Tropez)",
    badges: [
      "Marines de Cogolin",
      "Transfert aéroport de Nice",
      "Chauffeurs bilingues",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Cogolin",
    items: [
      {
        title: "Porte du golfe",
        description:
          "Position de carrefour entre la mer et l’arrière-pays : un point de départ pratique vers Saint-Tropez, Grimaud et les plages.",
      },
      {
        title: "Marines de Cogolin",
        description:
          "Desserte des quais et pontons de la marina, pour propriétaires, équipages et invités.",
      },
      {
        title: "Transfert longue distance",
        description:
          "Depuis l’aéroport de Nice, environ 1h20 à 1h30 de route, gérées dans un confort haut de gamme.",
      },
      {
        title: "Ancrage local",
        description:
          "Basés sur la Côte d’Azur, note 5,0/5 sur 38 avis Google, chauffeurs bilingues FR/EN.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Cogolin",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Liaison Cogolin ⇄ aéroport de Nice Côte d’Azur avec suivi de vol ; trajet plus long qu’au départ de Cannes, calé dès la prise en charge.",
      },
      {
        title: "Gare TGV de Saint-Raphaël",
        text: "Prise en charge à la gare de Saint-Raphaël, à environ 30 à 40 minutes, comme alternative au train.",
      },
      {
        title: "Marines de Cogolin",
        text: "Transfert vers la marina et ses pontons, en coordination avec la conciergerie et les équipages.",
      },
      {
        title: "Mise à disposition",
        text: "À l’heure ou à la journée pour le village, les plages du golfe et Saint-Tropez.",
      },
      {
        title: "Longue distance",
        text: "Trajets toute distance depuis Cogolin : Cannes, Nice, Monaco et au-delà, avec un chauffeur dédié.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Vieux village de Cogolin",
        description:
          "Cœur provençal, place de la mairie et ateliers d’artisans.",
      },
      {
        name: "Marines de Cogolin",
        description: "Port de plaisance aux pontons et quais résidentiels.",
      },
      {
        name: "Manufactures (tapis & pipes)",
        description:
          "Traditions locales du tapis de Cogolin et de la pipe de bruyère.",
      },
      {
        name: "Saint-Tropez",
        description: "Port et village voisins, à quelques minutes.",
      },
      {
        name: "Gare de Saint-Raphaël",
        description: "Gare TGV à environ 30-40 min, point de transfert.",
      },
      {
        name: "Aéroport de Nice",
        description: "Aéroport Nice Côte d’Azur, à environ 1h20-1h30.",
      },
    ],
  },
  generic: {
    heading: "Un chauffeur privé pour Cogolin",
    subheading:
      "De la porte du golfe aux Marines de Cogolin, un service premium vers Saint-Tropez et les plages.",
    paragraphs: [
      "Cogolin fait figure de porte du golfe de Saint-Tropez : ville provençale vivante, connue pour ses manufactures de tapis et ses pipes de bruyère, elle relie l’arrière-pays à la mer et aux Marines de Cogolin, son port de plaisance. C’est un point d’ancrage pratique pour rayonner vers Saint-Tropez, Grimaud et les plages.",
      "Comme tout le golfe, Cogolin n’a pas d’aéroport : l’arrivée se fait le plus souvent par Nice Côte d’Azur, à environ 1h20 à 1h30 de route selon le trafic estival. Nous calons l’heure de départ pour limiter l’attente, et proposons la gare TGV de Saint-Raphaël, à une trentaine de minutes, comme point de transfert alternatif.",
      "Pour les résidents des Marines, les équipages et la clientèle des villas, un même chauffeur assure transferts et mise à disposition, avec discrétion et horaires souples en haute saison. Forfaits fixes définis à l’avance ou à l’heure : devis immédiat.",
    ],
    bullets: [
      "Desserte des Marines de Cogolin",
      "Transfert aéroport de Nice & gare de Saint-Raphaël",
      "Point de départ vers Saint-Tropez",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Cogolin",
    steps: [
      "Indiquez votre trajet (village, Marines, aéroport, gare) et vos horaires.",
      "Nous recommandons le véhicule et le forfait adaptés.",
      "Le chauffeur suit votre vol ou votre train et ajuste le départ.",
      "Transfert direct ou mise à disposition pour le golfe pendant votre séjour.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Cogolin"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice depuis Cogolin ?",
      a: "Comptez environ 1h20 à 1h30 hors affluence, davantage lors des week-ends et pics de trafic estivaux du golfe. Nous adaptons l’heure de départ et suivons votre vol.",
    },
    {
      q: "Desservez-vous la gare TGV de Saint-Raphaël ?",
      a: "Oui, la gare de Saint-Raphaël se situe à environ 30 à 40 minutes de Cogolin ; c’est un point de transfert alternatif pratique pour les arrivées et départs en train.",
    },
    {
      q: "Assurez-vous la liaison avec Saint-Tropez ?",
      a: "Oui, Saint-Tropez est à quelques minutes ; nous assurons les allers-retours vers le port, les restaurants et les soirées, avec retour de nuit.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "En haute saison estivale, réservez au plus tôt : la demande est forte dans le golfe de Saint-Tropez. Nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale et aux séjours en villa.",
    },
    {
      q: "Quels véhicules proposez-vous ?",
      a: "Berline Mercedes, van premium (Classe V), SUV et minibus selon le nombre de passagers et les bagages, adaptés à la longue distance depuis Nice ou Cannes.",
    },
    {
      q: "Desservez-vous les Marines de Cogolin ?",
      a: "Oui, nous assurons les transferts vers la marina, ses quais et pontons, pour les propriétaires, les équipages et leurs invités, en coordination avec la conciergerie.",
    },
  ],
  relatedLinks: [
    {
      label: "Chauffeur Saint-Tropez, tarifs et trajets",
      href: "/chauffeur/saint-tropez",
    },
    { label: "Chauffeur privé à Cannes", href: "/chauffeur/cannes" },
    {
      label: "Chauffeur privé sur la Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    {
      label: "Location de véhicule à Saint-Tropez",
      href: "/location/location-voiture-saint-tropez",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Cogolin",
    text: "Transfert aéroport de Nice, gare de Saint-Raphaël ou Marines de Cogolin : réservez votre chauffeur, réponse rapide 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Cogolin — VTC & transfert aéroport",
    description:
      "Chauffeur privé et VTC à Cogolin : transfert aéroport de Nice, gare de Saint-Raphaël, Marines de Cogolin, mise à disposition et liaison Saint-Tropez.",
    canonical: "/chauffeur/cogolin",
  },
};

export default cogolin;
